// Grid-preference PRECEDENCE — PURE, and a LEAF module (imports one type).
//
// A saved dashboard view can come from two places now:
//
//   · the user's OWN preferences (auto-saved on every drag / toggle / config —
//     unchanged behaviour), and
//   · an admin-set TEAM DEFAULT for that grid, which everyone inherits until
//     they personalise.
//
// The chain is PERSONAL → TEAM DEFAULT → BUILT-IN, resolved per gridKey. The
// built-in is not represented here at all: it is whatever each dashboard's own
// decoder produces from an absent envelope, so "no personal and no team
// default" simply means this module returns nothing for that key and the client
// falls through to its own defaults exactly as it always has.
//
// ⚠️⚠️ "NO PERSONAL PREF" MUST KEEP MEANING "HASN'T CUSTOMISED". The whole
// inheritance model rests on it: if merely LOADING a dashboard wrote a personal
// pref, every user would be pinned to whatever they first opened and a later
// team-default change would reach nobody. So the personal pref is written only
// from a real user change, and a test guards that the pages never save on mount.
//
// ⚠️ FIRST PERSONALISATION SEEDS FROM WHAT THE USER IS ALREADY SEEING. Because
// the resolver hands the client the EFFECTIVE envelope — the team default, when
// the user has no personal pref — and every dashboard decodes that envelope into
// its state and re-encodes the WHOLE state on any change, a user who drags one
// panel persists the team default plus that change. Their first personalisation
// cannot silently reset the rest of the view to built-in. That property is a
// consequence of resolving to the EFFECTIVE envelope rather than to a flag, so
// do not "optimise" this into returning only the personal slice.
//
// FILTERS ARE NOT IN HERE, deliberately: date range, brand/AM/group, the
// name-contains boxes are per-session query state, never a saved view. Only the
// display config rides `gridPreferences`.

import type {
  GridPreferenceSourcesByKey,
  GridPreferencesByKey,
} from '../types/user.js';

/**
 * Resolve the EFFECTIVE per-grid preferences: the user's own where they have
 * one, otherwise the team default. Returns `undefined` when neither side has
 * anything at all, matching the existing "absent → built-in" contract.
 *
 * ⚠️ Resolution is PER GRID KEY, not whole-envelope. A user who has customised
 * the accounts grid but never touched Google Ads must still inherit the Google
 * Ads team default — an all-or-nothing rule would strand them on built-in for
 * every grid they hadn't personally saved.
 */
export function resolveEffectiveGridPreferences(
  personal: GridPreferencesByKey | undefined,
  team: GridPreferencesByKey | undefined,
): GridPreferencesByKey | undefined {
  const merged: GridPreferencesByKey = { ...(team ?? {}) };
  for (const [gridKey, prefs] of Object.entries(personal ?? {})) {
    merged[gridKey] = prefs;
  }
  return Object.keys(merged).length > 0 ? merged : undefined;
}

/**
 * Which source each resolved key came from. Personal wins wherever it exists,
 * so this is exactly the inverse view of the merge above.
 */
export function resolveGridPreferenceSources(
  personal: GridPreferencesByKey | undefined,
  team: GridPreferencesByKey | undefined,
): GridPreferenceSourcesByKey | undefined {
  const out: GridPreferenceSourcesByKey = {};
  for (const gridKey of Object.keys(team ?? {})) out[gridKey] = 'team';
  for (const gridKey of Object.keys(personal ?? {})) out[gridKey] = 'personal';
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Has this user personalised this grid? Drives whether "Reset" has anything to
 * do — offering a control that would visibly do nothing is worse than saying so.
 */
export function hasPersonalGridPreference(
  sources: GridPreferenceSourcesByKey | undefined,
  gridKey: string,
): boolean {
  return sources?.[gridKey] === 'personal';
}
